import { useTranslation } from "react-i18next";
import { EsfStationMCore, EsfStationSizes } from "./esfStationCore";
import { colors, fonts, glyphs } from "../../../Styles";
import NewButton from "../../BaseComponents/Buttons/newButton";
import AnimalNumberDisplay, { Themes } from "../../Animals/animalNumberDisplay";
import { Station } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/queryStations";
import { useStationStatusById } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationStatusSlice";
import { useAnimalById } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/AnimalDataSlices/animalsSlice";
import { WaitAnimation } from "../../BaseComponents";
import { useNavigate } from "react-router";
import { Pages, NavigationFunction as RootNavigation, } from "../../../Navigation/root";
import { useAppDispatch, useAppSelector } from "../../../Store";
import { useStationVisitsById } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationVisitsSlice";
import { useEffect, useRef } from "react";
import { fetchStationStatusByStationIdsThunk, fetchStationVisitCountByStationIdsThunk, fetchStationVisitsByStationIdsThunk } from "../../../Store/Thunks/stationStatusByIds";
import { useStationById } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationSlice";
import { EsfStationCoreWalkIn } from "./esfStationCoreWalkIn";

interface EsfStationModelProps {
  components?: boolean
  feedMotor?: number
  station: number
}

export function EsfStationEnhanced({ station, components, feedMotor }:EsfStationModelProps) {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const dispatch = useAppDispatch();
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  
  const { isLoading: isStationLoading, entity: thisStation } = useStationById(station); 
  const { isLoading: isStationStatusLoading, entity: stationStatus } = useStationStatusById(station); 
  const { isLoading: isStationVisitsLoading, entity: stationVisits } = useStationVisitsById(station); 
  const { isLoading: isAnimalLoading, entity: animal } = useAnimalById(stationStatus?.animalId);
  const { isLoading: isPrevAnimalLoading, entity: prevAnimal } = useAnimalById(stationVisits?.lastVisits[0].fk_animals);

  const prevAnimalRef = useRef<typeof animal>(undefined);

  useEffect(() => {
    const prevAnimal = prevAnimalRef.current;
  
    if (prevAnimal && !animal) {
      dispatch(fetchStationVisitsByStationIdsThunk([station]));
    }
  
    prevAnimalRef.current = animal;
  }, [animal, station, dispatch]);

  let state: number = 0;
  let content: string | number | JSX.Element = "";
  let secondaryContent: string = "";

  if (isStationStatusLoading) {
    state = 0;
    content = <WaitAnimation />;
    secondaryContent = "";
  } else if (!isStationStatusLoading && stationStatus) {
    const { animalId, responder_number } = stationStatus;
    if (animalId != null) {
      state = isAnimalLoading ? 0 : 1;
      content = isAnimalLoading ? <WaitAnimation/> : `${t("animalPages.animal.animal", { defaultValue: "Animal" })} ${animal?.animal_number.toString()}`
      secondaryContent = responder_number ? responder_number.toString() : "";
    } else if (responder_number) {
      state = 2;
      content = t("esfWalkThroughPages.sections.unknownAnimal", { defaultValue: "unknown animal" });
      secondaryContent = responder_number.toString();
    } else {
      state = 0;
      content = t("esfWalkThroughPages.sections.noAnimal", { defaultValue: "No animal" });
      secondaryContent = "";
    }
  } else {
    state = 0;
    content = t("esfWalkThroughPages.sections.noAnimal", { defaultValue: "No animal" });
    secondaryContent = "";
  }

  let prevAnimalContent: string = "";

  if (isPrevAnimalLoading) {
    prevAnimalContent = "";
  } else if (prevAnimal) { 
    prevAnimalContent = `${t("animalPages.animal.animal", { defaultValue: "Animal" })} ${prevAnimal?.animal_number.toString()}`
  } else {
    prevAnimalContent = "";
  }
  
  return (
    <div style={{position: "relative", width: "fit-content", height: "fit-content"}}>
      {thisStation?.stationType === 2 ? <EsfStationMCore size={EsfStationSizes.normal} pins={{ components: components, feedMotor: feedMotor, gateMotor: stationStatus?.gateState }} />
      : <EsfStationCoreWalkIn size={EsfStationSizes.normal} pins={{components: components, feedMotor: feedMotor }} />}
      <div style={{position: "absolute", top: thisStation?.stationType === 2 ? "62%" : "68%", left: thisStation?.stationType === 2 ? "55%" : "52%", transform: "translate(-50%, -50%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "5px"}}>
        <AnimalNumberDisplay
          state={state}
          content={content}
          secondaryContent={secondaryContent}
          header={t("esfWalkThroughPages.sections.inThisStation", { defaultValue: "in this station" })}
          theme={Themes.lightmode}
          style={{ backgroundColor: colors.graphBlueSeven, padding: "5px 10px", borderRadius: "10px" }}
          onClick={() => RootNavigation({ Navigator: navigator, navigation_page: Pages.animals, navigation_tab: Pages.animal, replace: true, navArgs: { selected: stationStatus?.animalId }, currentDomain: farm })}
        />
        { prevAnimal && <div style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "5px"}}>
          <p style={{ ...fonts.fontSize3, ...fonts.header, color: colors.graphBlueSeven }}>{`${t("esfWalkThroughPages.sections.previously", {defaultValue: "previously"})}`}</p>
          <p style={{ ...fonts.fontSize3, ...fonts.header, color: colors.greyLightTertiary }}>{prevAnimalContent}</p>
        </div>}
      </div>
    </div>
  )
}